<script setup lang="ts">
const { t } = useTexts();
const { closeModal, openToaster } = useLayoutStore();
const { updateUserContract } = useUserDataStore();
const { userCountry, fetchUserProfile } = useUserData();
const { isAuthenticated } = storeToRefs(useAuthStore());

const { isContractsModalEnabled } = useFeatureFlagsJson();

const { isUserMigrationEnabled } = useFeatureFlagsNew();

const {
  hasAnyContractPending,
  isUserCompleteRegistration,
  redirectToPendingSettings,
} = useUserValidations();

const isLoading = ref<boolean>(false);

async function updateContractStatus() {
  if (!allContractsAccepted.value) {
    return;
  }

  isLoading.value = true;

  await updateUserContract('all')
    .then(async () => {
      await fetchUserProfile({ force: true });

      const message = isUserCompleteRegistration.value
        ? t('account.contracts_modal.update_success')
        : t('account.contracts_modal.update_success_redirect');

      openToaster({
        message,
        type: 'success',
      });

      closeModal();

      if (!isUserCompleteRegistration.value) {
        await redirectToPendingSettings();
      }
    })
    .catch((err) => {
      const errorData = err?.data?.message;

      const errorMessage =
        errorData || t('account.contracts_section.update_error');

      openToaster({
        message: errorMessage,
        type: 'error',
      });

      isLoading.value = false;
    });
}

const allContractsAccepted = ref(false);

watch(
  hasAnyContractPending,
  (value) => {
    if (
      !value ||
      !isContractsModalEnabled.value ||
      !isAuthenticated.value ||
      userCountry.value !== 'BRA'
    ) {
      closeModal();
    }
  },
  { immediate: true },
);

watch(
  isAuthenticated,
  (value) => {
    if (!value) {
      closeModal();
    }
  },
  { immediate: true },
);
</script>

<template>
  <div :class="$style.contractsModal">
    <BaseLoading
      class="!bg-auth-bg-primary !absolute !z-20"
      :icon-loader="true"
      :show="isLoading"
    />

    <img
      alt="Email icon"
      aria-hidden="true"
      :class="$style.modalIcon"
      :src="getAssetsUrlForCDN('/assets/images/svg/empty.svg')"
    />

    <div :class="$style.title">
      {{ $t('account.contracts_modal.title') }}
    </div>

    <div :class="$style.subtitle">
      {{ $t('account.contracts_modal.subtitle') }}
    </div>

    <div :class="$style.contractField">
      <span>
        <BaseFormToggle
          v-model="allContractsAccepted"
          :class="$style.switch"
        />
      </span>
      <div>
        {{ $t('account.contracts_modal.accept_contracts') }}
        <NuxtLink
          target="_blank"
          to="/page/terms"
        >
          {{ $t('account.contracts_modal.terms_and_conditions') }}
        </NuxtLink>
        {{
          isUserMigrationEnabled
            ? $t('account.contracts_modal.and')
            : $t('account.contracts_modal.and_a')
        }}
        <NuxtLink
          target="_blank"
          to="/page/privacy"
        >
          {{ $t('account.contracts_modal.privacy_policy') }}
        </NuxtLink>
        <template v-if="isUserMigrationEnabled">
          {{ $t('account.contracts_modal.and_the') }}
          <NuxtLink
            target="_blank"
            to="/page/consent"
          >
            {{ $t('account.contracts_modal.consent_term') }}
          </NuxtLink>
          {{ $t('account.contracts_modal.migrate_account') }}
        </template>
      </div>
    </div>

    <div :class="$style.buttons">
      <button
        :class="[
          $style.sendButton,
          { [$style.disable]: !allContractsAccepted },
        ]"
        :disabled="!allContractsAccepted"
        @click="updateContractStatus"
      >
        <NuxtIcon name="check" />
        {{ $t('account.contracts_modal.button_ok') }}
      </button>

      <button
        :class="[
          $style.closeButton,
          { [$style.disable]: allContractsAccepted },
        ]"
        :disabled="allContractsAccepted"
        @click="closeModal"
      >
        <NuxtIcon name="close" />
        {{ $t('account.contracts_modal.later') }}
      </button>
    </div>
  </div>
</template>

<style module lang="scss">
.contractsModal {
  @apply p-8 md:p-10 w-full h-full flex flex-col justify-center items-center gap-5 text-auth-texts max-w-[450px] mx-auto;

  .modalIcon {
    @apply w-28 h-28 mx-auto select-none;
  }

  .title {
    @apply text-xl font-bold text-auth-texts;
  }

  .subtitle {
    @apply text-sm opacity-80 text-center;
  }

  .contractField {
    @apply w-full flex items-center justify-center gap-3;

    div {
      @apply text-xs leading-4;

      a {
        @apply text-primary border-b border-primary border-dotted;
      }

      strong {
        @apply font-semibold;
      }
    }
  }

  .buttons {
    @apply w-full flex items-center justify-center flex-col gap-4;

    button {
      @apply py-2.5 px-3 rounded-md flex items-center justify-center gap-2 text-sm bg-auth-button-bg text-auth-button-text font-bold flex-1 w-full cursor-pointer;

      &.closeButton {
        @apply text-auth-texts/50 bg-transparent text-2xs font-normal leading-3 flex items-center justify-center gap-1 p-0;
      }

      &.disable {
        @apply opacity-40 cursor-not-allowed;
      }
    }
  }
}
</style>
